import React, { ReactElement, useEffect, useState } from 'react';
import {
  Col,
  Text,
  Loader,
  LoaderButton,
  Row,
  usePageNumberFromUrl,
  usePerPageValueFromUrl,
  useSortValueFromUrl,
} from 'idsk-react-components';
import { MobileFilterModal, Pagination, useResponsiveContext } from 'slovlex-idsk-react-components';
import { useSearchParams } from 'react-router-dom';
import FilterTagsComponent from './component/FilterTagsComponent';
import SidePanel from './component/SidePanel';
import { SafeParameterSetter } from '../../model/SafeParameterSetter';
import getUpdatedSearchParams from './function/getUpdatedSearchParams';
import { defaultPerPageValue, perPageDataArray } from '../../constants/PaginationPerPageConstants';
import PublikovanyTerminFilter from '../../model/PublikovanyTerminFilter';
import usePublikovanyTerminFilterList from './hook/usePublikovanyTerminFilterList';
import getPaginationTotalLabel from '../../functions/getPaginationTotalLabel';
import PublikovanyTerminTable from './component/PublikovanyTerminTable';
import './publikovanyTerminList.scss';
import emptyFilter from './emptyFilter';
import { useClient, useClientStatusContext } from '../../context/ClientStatusContext';
import { exportTerminList } from '../../client/export/exportTerminListClient';
import { terminListErrorStatus } from '../../constants/ErrorConstant';
import downloadFile from '../../functions/downloadFile';
import countActiveFilters from '../../functions/countActiveFilters';
import PublikovanyTerminListSort from './component/PublikovanyTerminListSort';
import SearchInput from './component/SearchInput';
import MobileFilters from './component/MobileFilters';
import PublikovanyTerminMobileTableItem from './component/PublikovanyTerminMobileTableItem';

interface Props {
  initialFilter: PublikovanyTerminFilter;
}

const PublikovanyTerminList = ({
  initialFilter,
}: Props): ReactElement => {
  const [firstLoad, setFirstLoad] = useState(true);
  const { page } = usePageNumberFromUrl('page');
  const { perPageValue } = usePerPageValueFromUrl('perPage', perPageDataArray, defaultPerPageValue);
  const [searchParams, setSearchParams] = useSearchParams();
  const { sort } = useSortValueFromUrl();
  const [filter, setFilter] = useState<PublikovanyTerminFilter>(initialFilter);
  const [filterCopy, setFilterCopy] = useState<PublikovanyTerminFilter>(filter);
  const {
    publikovanyTerminPage,
    requestStatus,
  } = usePublikovanyTerminFilterList(filter);
  const { isSmallScreen } = useResponsiveContext();
  const [isOpenModal, setIsOpenModal] = useState(false);

  const onUpdateField: SafeParameterSetter<PublikovanyTerminFilter> = (fieldName, value): void => {
    setFilter({
      ...filterCopy,
      [fieldName]: value,
    });
    setFilterCopy({
      ...filterCopy,
      [fieldName]: value,
    });
  };

  const onUpdateFieldCopy: SafeParameterSetter<PublikovanyTerminFilter> = (fieldName, value): void => {
    setFilterCopy(prevState => ({
      ...prevState,
      [fieldName]: value,
    }));
  };

  const onRecordsPerPageChange = (value: number): void => {
    const updatedSearchParams = new URLSearchParams(searchParams.toString());
    updatedSearchParams.set('perPage', value.toString());
    setSearchParams(updatedSearchParams.toString());
  };

  useEffect(() => {
    if (Array.from(searchParams.values()).length === 0
      && JSON.stringify(filter) !== JSON.stringify(emptyFilter)) {
      setFilter(emptyFilter);
      setFilterCopy(emptyFilter);
      setFirstLoad(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    if (!firstLoad) {
      const urlParamsFromFilter = getUpdatedSearchParams(1, perPageValue, sort, filter);
      setSearchParams(urlParamsFromFilter);
    }
    setFirstLoad(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(filter)]);

  const { handleStatus } = useClientStatusContext();

  const {
    wrapClient: exportTermin,
    status: exportTerminStatus,
  } = useClient(exportTerminList);

  useEffect(() => {
    if (exportTerminStatus.responseCode === 404) {
      handleStatus(terminListErrorStatus);
    }
  }, [exportTerminStatus, handleStatus]);

  const onExportToPdf = async (e): Promise<void> => {
    e.preventDefault();
    const { data } = await exportTermin(
      filter.tezaurus?.uuid || '',
      filter.searchTextHladanyTermin,
      filter.searchTextLegalnyZdroj,
      filter.searchTextDoktrinalnyZdroj,
      filter.pouzitiePredpisList,
    );

    if (data.blob) {
      downloadFile(data.fileName, data.blob);
    }
  };

  const handleCloseModal = ():void => {
    setIsOpenModal(false);
    window.parent.postMessage({
      type: 'iframe-to-parent-filter-modal-close',
    }, '*');
    setFilter(filterCopy);
  };

  const handleOpenModal = ():void => {
    setIsOpenModal(true);
    window.parent.postMessage({
      type: 'iframe-to-parent-filter-modal-open',
    }, '*');
    setFilter(filterCopy);
  };

  const clearFilters = (): void => {
    setFilter(emptyFilter);
    setFilterCopy(emptyFilter);
  };

  // tlacidlo na vymazanie filtra sa podla zadania zobrazuje az pri dvoch a viac kriteriach
  const onClearFilters = countActiveFilters(filter) >= 2 ? clearFilters : null;

  return (
    <div className="publikovany-termin-list">
      <Row className="export-button__row">
        <Col width="one-quarter">
          <LoaderButton
            disabled={publikovanyTerminPage?.totalElements === 0}
            className="export-button"
            loading={exportTerminStatus.loading}
            size="large"
            buttonStyle="third"
            type="button"
            onClick={(event) => onExportToPdf(event)}
          >Exportovať termíny (PDF)
          </LoaderButton>
        </Col>
      </Row>
      {isSmallScreen && (
      <SearchInput
        filterCopy={filterCopy}
        label="Hľadaný termín"
        filterSearchText={filterCopy?.searchTextHladanyTermin}
        fieldName="searchTextHladanyTermin"
        onUpdateFieldCopy={onUpdateFieldCopy}
        setFilter={setFilter}
        className="slovlex-idsk-side-panel-input-wrapper termin-container"
        maxLength={764}
      />
      )}
      <MobileFilterModal
        handleClose={handleCloseModal}
        handleOpen={handleOpenModal}
        isOpen={isOpenModal}
        numberOfResults={publikovanyTerminPage?.totalElements || 0}
        filters={(
          <MobileFilters
            setFilter={setFilter}
            filterCopy={filterCopy}
            onUpdateFieldCopy={onUpdateFieldCopy}
            filter={filter}
            onUpdateField={onUpdateField}
          />
          )}
        perPageDataArray={perPageDataArray}
        onPerPageChange={onRecordsPerPageChange}
        perPageValue={perPageValue}
        clearFilters={onClearFilters}
        filterTags={(
          <FilterTagsComponent
            filter={filter}
            onUpdateField={onUpdateField}
          />
          )}
      />
      {!isOpenModal && (
      <Row className="prispevok-vestnika-list-component__results-row">
        {!isSmallScreen && (
        <Col className="side-panel-column" width="one-quarter">
          <SidePanel
            setFilter={setFilter}
            filterCopy={filterCopy}
            onUpdateFieldCopy={onUpdateFieldCopy}
            filter={filter}
            onUpdateField={onUpdateField}
          />
        </Col>
        )}
        <Col width="three-quarters">
          <FilterTagsComponent
            filter={filter}
            onUpdateField={onUpdateField}
            onClearFilters={onClearFilters}
          />
          <Loader loading={requestStatus.loading}>
            <>
              {isSmallScreen && (
              <Row className="publikovany-termin-mobile-sort-select-row">
                <Col>
                  <PublikovanyTerminListSort />
                </Col>
              </Row>
              )}
              <Row className="pagination-label-sort-row">
                <Col
                  width="full"
                  widthDesktop="one-third"
                  className={isSmallScreen && 'pagination-label-mobile'}
                >
                  <Text className="pagination-label">{getPaginationTotalLabel(publikovanyTerminPage, page)}</Text>
                </Col>
                {!isSmallScreen && (
                <Col width="full" widthDesktop="two-thirds">
                  <PublikovanyTerminListSort />
                </Col>
                )}
              </Row>
              {publikovanyTerminPage && (
              <div className="publikovany-termin-list-component">
                {isSmallScreen ? (
                  <div className="publikovany-termin-list-component">
                    {publikovanyTerminPage.terminList.map(item => (
                      <PublikovanyTerminMobileTableItem
                        key={`publikovanyTerminTableComponentKey-${item.uuid}`}
                        termin={item.termin}
                        preferovanyTermin={item.preferovanyTermin}
                        tezaurus={item.tezaurus}
                        uuid={item.termin === item.preferovanyTermin
                          ? item.uuid
                          : item.terminUuid}
                      />
                    ))}
                  </div>
                ) : (
                  <PublikovanyTerminTable
                    isSelectedFilter={JSON.stringify(filter) !== JSON.stringify(emptyFilter)}
                    terminPage={publikovanyTerminPage}
                  />
                )}
                <Row className="publikovany-termin-list-pagination-row">
                  <Col>
                    <Pagination
                      recordsPerPage={perPageDataArray}
                      hideTotal
                      number={page}
                      size={publikovanyTerminPage.size}
                      totalElements={publikovanyTerminPage.totalElements}
                      perPageValue={perPageValue}
                      onRecordsPerPageChange={onRecordsPerPageChange}
                    />
                  </Col>
                </Row>
              </div>
              )}
            </>
          </Loader>
        </Col>
      </Row>
      )}
    </div>
  );
};

export default PublikovanyTerminList;
